import { Listbox, Transition } from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon as SelectorIcon,
} from "@heroicons/react/20/solid";
import { Field, useField, useFormikContext } from "formik";
import { Fragment } from "react";
import { twMerge } from "tailwind-merge";

const months = [
  { id: null, name: "--" },
  { id: 1, name: "January" },
  { id: 2, name: "February" },
  { id: 3, name: "March" },
  { id: 4, name: "April" },
  { id: 5, name: "May" },
  { id: 6, name: "June" },
  { id: 7, name: "July" },
  { id: 8, name: "August" },
  { id: 9, name: "September" },
  { id: 10, name: "October" },
  { id: 11, name: "November" },
  { id: 12, name: "December" },
];
const years = [
  null,
  ...Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 1 + i),
];

const MonthDropdown = () => {
  const monthLabel = (val) => {
    return months.find((month) => month.id === val)?.name || "";
  };
  const [field, _meta, helpers] = useField("cohort_month");

  const selected = field.value;
  const onChange = (val) => {
    helpers.setValue(val);
  };
  return (
    <Listbox value={selected} onChange={onChange} id="monthsel">
      {({ open }) => (
        <>
          <div className="relative mt-1">
            <Listbox.Button className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm">
              <span className="block truncate">{monthLabel(selected)}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <SelectorIcon
                  className="size-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                {months.map((month) => {
                  return (
                    <Listbox.Option
                      key={month.id}
                      className={({ active }) =>
                        twMerge(
                          active ? "bg-indigo-600 text-white" : "text-gray-900",
                          "relative cursor-default select-none py-2 pl-3 pr-9",
                        )
                      }
                      value={month.id}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={twMerge(
                              selected ? "font-semibold" : "font-normal",
                              "block truncate",
                            )}
                          >
                            {month.name}
                          </span>

                          {selected ? (
                            <span
                              className={twMerge(
                                active ? "text-white" : "text-indigo-600",
                                "absolute inset-y-0 right-0 flex items-center pr-4",
                              )}
                            >
                              <CheckIcon
                                className="size-5"
                                aria-hidden="true"
                              />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  );
                })}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
    // {/* <FieldGroup label="Month">
    // </FieldGroup> */}
  );
};

const YearDropdown = () => {
  const [field, _meta, helpers] = useField("cohort_year");

  const selected = field.value;
  const onChange = (val) => {
    helpers.setValue(val);
  };
  return (
    <Listbox value={selected} onChange={onChange} id="yearsel">
      {({ open }) => (
        <>
          <div className="relative mt-1">
            <Listbox.Button className="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm">
              <span className="block truncate">{selected ?? "--"}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <SelectorIcon
                  className="size-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                {years.map((year) => {
                  return (
                    <Listbox.Option
                      key={year ?? "none"}
                      className={({ active }) =>
                        twMerge(
                          active ? "bg-indigo-600 text-white" : "text-gray-900",
                          "relative cursor-default select-none py-2 pl-3 pr-9",
                        )
                      }
                      value={year}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={twMerge(
                              selected ? "font-semibold" : "font-normal",
                              "block truncate",
                            )}
                          >
                            {year ?? "--"}
                          </span>

                          {selected ? (
                            <span
                              className={twMerge(
                                active ? "text-white" : "text-indigo-600",
                                "absolute inset-y-0 right-0 flex items-center pr-4",
                              )}
                            >
                              <CheckIcon
                                className="size-5"
                                aria-hidden="true"
                              />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  );
                })}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
    // {/* <FieldGroup label="Month">
    // </FieldGroup> */}
  );
};
const CohortInfo = ({ required = false }) => {
  const { values } = useFormikContext();

  return (
    <>
      <div className="mt-4 text-blue-400">Cohort Date</div>
      <div className="mb-4 flex flex-col">
        <div className="flex flex-col pt-2 text-justify text-sm text-gray-400">
          {!required && (
            <div className="grow text-left">
              <Field
                type="checkbox"
                id="cohortCheckbox"
                name="cohort"
                className="mx-1 cursor-pointer px-1"
              />
              <label
                htmlFor="cohortCheckbox"
                className="mx-1 cursor-pointer px-1"
              >
                Add Corhort Date
              </label>
            </div>
          )}
          {(values.cohort || required) && (
            <>
              <MonthDropdown />
              <YearDropdown />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CohortInfo;
